// src/utils/auditLog.js
// Audit logging utility for tracking all user actions across the application
import { collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import { db, auth } from "../firebase";
import { sendAuditEvent } from './auditWriterClient';

/**
 * Log an action to the audit log
 * @param {string} action - The action performed (e.g., 'CREATE_MELDING', 'UPDATE_ROBOT', 'DELETE_ONDERDEEL')
 * @param {string} resourceType - Type of resource (e.g., 'melding', 'robot', 'onderdeel', 'bijzonderheid')
 * @param {string} resourceId - ID of the affected resource
 * @param {string} branchId - Branch/vestiging ID where action occurred
 * @param {object} metadata - Additional metadata about the action
 * @param {object} changes - Object describing what changed (before/after values)
 */
export async function logAuditEvent(action, resourceType, resourceId, branchId, metadata = {}, changes = null) {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('[AuditLog] No authenticated user - audit event not logged:', {
        action,
        resourceType,
        resourceId,
        branchId
      });
      return;
    }

    const payload = {
      action,
      resourceType,
      resourceId,
      branchId,
      metadata: metadata || {},
      changes: changes || null,
      userAgent: navigator.userAgent
    };

    await sendAuditEvent(payload);
    console.log(`[AuditLog] Dispatched audit event: ${action} on ${resourceType}/${resourceId}`);
  } catch (error) {
    // Don't fail the main operation if audit logging fails
    console.error('[AuditLog] Failed to log audit event:', error);
  }
}

export async function logSystemAuditEvent({
  action,
  actionLabel,
  resourceType,
  resourceId = null,
  branchId = null,
  branchName = null,
  status = 'success',
  summary = '',
  details = {},
  counts = {},
  before = null,
  after = null,
  triggeredBy = 'background-cleanup',
  actorType = 'system',
  actorName = 'Automatische taak',
} = {}) {
  const metadata = {
    actionLabel: actionLabel || getAuditActionLabel(action),
    actorType,
    actorId: 'system',
    actorName,
    actorEmail: null,
    branchName,
    status,
    summary,
    details,
    counts,
    before,
    after,
    triggeredBy,
  };

  return logAuditEvent(
    action,
    resourceType,
    resourceId,
    branchId,
    metadata,
    createChangesObject(before, after)
  );
}

/**
 * Get audit logs with filtering options (super admin only)
 * @param {object} filters - Filtering options
 * @returns {Promise<Array>} Array of audit log entries
 */
export async function getAuditLogs(filters = {}) {
  try {
    const {
      branchId = null,
      userId = null,
      resourceType = null,
      action = null,
      startDate = null,
      endDate = null,
      limitCount = 100
    } = filters;

    let q = collection(db, "auditLogs");
    const constraints = [];

    if (branchId) {
      constraints.push(where("branchId", "==", branchId));
    }
    if (userId) {
      constraints.push(where("userId", "==", userId));
    }
    if (resourceType) {
      constraints.push(where("resourceType", "==", resourceType));
    }
    if (action) {
      constraints.push(where("action", "==", action));
    }
    if (startDate) {
      constraints.push(where("timestamp", ">=", startDate));
    }
    if (endDate) {
      constraints.push(where("timestamp", "<=", endDate));
    }

    constraints.push(orderBy("timestamp", "desc"));
    constraints.push(limit(limitCount));

    q = query(q, ...constraints);
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('[AuditLog] Failed to fetch audit logs:', error);
    return [];
  }
}

const PARTS_MUTATION_ACTIONS = new Set([
  'CREATE_ONDERDEEL',
  'UPDATE_ONDERDEEL',
  'DELETE_ONDERDEEL',
  'ADD_ONDERDEEL_STOCK',
  'USE_ONDERDEEL',
  'addOnderdeel',
  'updateOnderdeel',
  'deleteOnderdeel',
  'RESTOCK',
  'USE',
]);

function auditTimestampMs(value) {
  if (typeof value?.toMillis === 'function') return value.toMillis();
  const parsed = new Date(value || 0).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export function normalizePartsModificationAudit(entry = {}) {
  const metadata = entry.metadata && typeof entry.metadata === 'object' ? entry.metadata : {};
  const timestamp = entry.timestamp || metadata.timestamp || null;
  const timestampMs = auditTimestampMs(timestamp);
  if (!timestampMs) return null;
  const actorName = String(
    entry.userName || metadata.actorName || metadata.userName || metadata.user || '',
  ).trim();
  const actorEmail = String(entry.userEmail || metadata.actorEmail || '').trim();
  return {
    timestamp,
    timestampMs,
    actorName: actorName || actorEmail || 'Onbekende gebruiker',
    actorEmail: actorEmail || null,
    action: String(entry.action || '').trim(),
    resourceId: String(entry.resourceId || metadata.code || '').trim() || null,
  };
}

export function isMeaningfulPartsMutationAudit(entry = {}) {
  const resourceType = String(entry.resourceType || '').trim().toLowerCase();
  const action = String(entry.action || '').trim();
  return resourceType === 'onderdeel' && PARTS_MUTATION_ACTIONS.has(action);
}

export async function getLatestPartsModification(branchId) {
  const normalizedBranch = String(branchId || '').trim();
  if (!normalizedBranch) return null;
  try {
    const base = collection(db, 'auditLogs');
    const scoped = query(
      base,
      where('branchId', '==', normalizedBranch),
      where('resourceType', '==', 'onderdeel'),
      orderBy('timestamp', 'desc'),
      limit(25),
    );
    const snapshot = await getDocs(scoped);
    const entry = (snapshot?.docs || []).map((item) => ({ id: item.id, ...item.data() }))
      .find(isMeaningfulPartsMutationAudit);
    return entry ? normalizePartsModificationAudit(entry) : null;
  } catch (error) {
    // The index may not exist in an older deployment. Read only the scoped
    // branch/resource set and select the newest valid mutation locally.
    try {
      const snapshot = await getDocs(query(
        collection(db, 'auditLogs'),
        where('branchId', '==', normalizedBranch),
        where('resourceType', '==', 'onderdeel'),
      ));
      return (snapshot?.docs || [])
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter(isMeaningfulPartsMutationAudit)
        .map(normalizePartsModificationAudit)
        .filter(Boolean)
        .sort((left, right) => right.timestampMs - left.timestampMs)[0] || null;
    } catch (fallbackError) {
      console.warn('[AuditLog] Failed to read latest parts modification:', fallbackError || error);
      return null;
    }
  }
}

/**
 * Helper to create a changes object for audit logging
 * @param {object} before - State before change
 * @param {object} after - State after change
 * @returns {object} Changes object with before/after
 */
export function createChangesObject(before, after) {
  return {
    before: before || null,
    after: after || null
  };
}

export function getAuditActionLabel(action) {
  const key = String(action || '').trim();
  const labels = {
    CREATE_MELDING: 'Melding aangemaakt',
    UPDATE_MELDING: 'Melding bijgewerkt',
    DELETE_MELDING: 'Melding verwijderd',
    CREATE_ROBOT: 'Robot aangemaakt',
    UPDATE_ROBOT: 'Robot bijgewerkt',
    ROBOT_MAINTENANCE_RESPONSIBILITY_UPDATED: 'Robot onderhoudsverantwoordelijkheid bijgewerkt',
    DELETE_ROBOT: 'Robot verwijderd',
    CREATE_ONDERDEEL: 'Onderdeel aangemaakt',
    UPDATE_ONDERDEEL: 'Onderdeel bijgewerkt',
    DELETE_ONDERDEEL: 'Onderdeel verwijderd',
    CREATE_BIJZONDERHEID: 'Bijzonderheid aangemaakt',
    UPDATE_BIJZONDERHEID: 'Bijzonderheid bijgewerkt',
    DELETE_BIJZONDERHEID: 'Bijzonderheid verwijderd',
    AUTO_DEACTIVATE_EXPIRED_BIJZONDERHEID: 'Verlopen bijzonderheid gedeactiveerd',
    CREATE_CHECKLIST: 'Checklistkopie aangemaakt',
    UPDATE_CHECKLIST: 'Checklist opgeslagen',
    GENERATE_CHECKLIST_PDF: 'Checklist-PDF gegenereerd',
    CHECKLIST_SENT_TO_ROBOT_DOSSIER: 'Checklist naar robotdossier verzonden',
    DOCUMENT_GENERATED: 'Document gegenereerd',
    REPORT_GENERATED: 'Rapport gegenereerd',
    REPORT_OPENED: 'Rapport geopend',
    REPORT_OPEN_FAILED: 'Rapport openen mislukt',
    DOCUMENT_TEMPLATE_SELECTED: 'Documentsjabloon geselecteerd',
    branch_administration_settings_updated: 'Vestiging administratie-instellingen bijgewerkt',
    hours_report_admin_export_generated: 'Urenrapport administratie-export gegenereerd',
    mail_sent: 'Mail verzonden',
    backup_completed: 'Back-up voltooid',
    deleted_old_meldingen: 'Oude meldingen verwijderd',
    persist_weekly_report_history: 'Wekelijks rapport opgeslagen',
    geometry_sync_completed: 'MyRobot-geometrie gesynchroniseerd',
  };
  return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()) || 'Onbekende actie';
}

export function getAuditActorType(logOrMetadata = {}) {
  const metadata = logOrMetadata?.metadata && typeof logOrMetadata.metadata === 'object' ? logOrMetadata.metadata : {};
  const raw = String(logOrMetadata?.actorType || metadata.actorType || '').trim().toLowerCase();
  if (['system', 'scheduled', 'automation', 'backend'].includes(raw)) return raw;
  const action = String(logOrMetadata?.action || '').toLowerCase();
  const actor = String(logOrMetadata?.actorName || metadata.actorName || logOrMetadata?.userName || logOrMetadata?.userEmail || logOrMetadata?.actor || '').toLowerCase();
  if (actor.includes('system') || actor.includes('systeem')) return 'system';
  if (actor.includes('automatische') || action.includes('persist_') || action.includes('backup_') || action.includes('geometry_')) return 'scheduled';
  return 'user';
}

// Common action types for consistency
export const AuditActions = {
  // Meldingen
  CREATE_MELDING: 'CREATE_MELDING',
  UPDATE_MELDING: 'UPDATE_MELDING',
  DELETE_MELDING: 'DELETE_MELDING',
  
  // Robots
  CREATE_ROBOT: 'CREATE_ROBOT',
  UPDATE_ROBOT: 'UPDATE_ROBOT',
  ROBOT_MAINTENANCE_RESPONSIBILITY_UPDATED: 'ROBOT_MAINTENANCE_RESPONSIBILITY_UPDATED',
  DELETE_ROBOT: 'DELETE_ROBOT',
  
  // Onderdelen
  CREATE_ONDERDEEL: 'CREATE_ONDERDEEL',
  UPDATE_ONDERDEEL: 'UPDATE_ONDERDEEL',
  DELETE_ONDERDEEL: 'DELETE_ONDERDEEL',
  ADD_ONDERDEEL_STOCK: 'ADD_ONDERDEEL_STOCK',
  USE_ONDERDEEL: 'USE_ONDERDEEL',
  SCAN_ONDERDEEL_QR: 'SCAN_ONDERDEEL_QR',
  
  // Bijzonderheden
  CREATE_BIJZONDERHEID: 'CREATE_BIJZONDERHEID',
  UPDATE_BIJZONDERHEID: 'UPDATE_BIJZONDERHEID',
  DELETE_BIJZONDERHEID: 'DELETE_BIJZONDERHEID',
  
  // Garanties
  CREATE_GARANTIE: 'CREATE_GARANTIE',
  UPDATE_GARANTIE: 'UPDATE_GARANTIE',
  DELETE_GARANTIE: 'DELETE_GARANTIE',
  
  // Facturen
  CREATE_FACTUUR: 'CREATE_FACTUUR',
  UPDATE_FACTUUR: 'UPDATE_FACTUUR',
  DELETE_FACTUUR: 'DELETE_FACTUUR',
  
  // Users
  CREATE_USER: 'CREATE_USER',
  UPDATE_USER: 'UPDATE_USER',
  DELETE_USER: 'DELETE_USER',
  UPDATE_USER_ROLE: 'UPDATE_USER_ROLE',
  
  // Branches
  CREATE_BRANCH: 'CREATE_BRANCH',
  UPDATE_BRANCH: 'UPDATE_BRANCH',
  DELETE_BRANCH: 'DELETE_BRANCH',
  BRANCH_ADMINISTRATION_SETTINGS_UPDATED: 'branch_administration_settings_updated',
  
  // Authentication
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  
  // Other
  EXPORT_DATA: 'EXPORT_DATA',
  HOURS_REPORT_ADMIN_EXPORT_GENERATED: 'hours_report_admin_export_generated',
  IMPORT_DATA: 'IMPORT_DATA',

  // History refresh
  REFRESH_HISTORY: 'REFRESH_HISTORY',

  // Checklists
  CREATE_CHECKLIST: 'CREATE_CHECKLIST',
  UPDATE_CHECKLIST: 'UPDATE_CHECKLIST',
  GENERATE_CHECKLIST_PDF: 'GENERATE_CHECKLIST_PDF',
  CHECKLIST_SENT_TO_ROBOT_DOSSIER: 'CHECKLIST_SENT_TO_ROBOT_DOSSIER',

  // Documents
  DOCUMENT_GENERATED: 'DOCUMENT_GENERATED',
  REPORT_GENERATED: 'REPORT_GENERATED',
  REPORT_OPENED: 'REPORT_OPENED',
  REPORT_OPEN_FAILED: 'REPORT_OPEN_FAILED',
  DOCUMENT_TEMPLATE_SELECTED: 'DOCUMENT_TEMPLATE_SELECTED'
};
